import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { AddToLibraryDialog } from "../components/AddToLibraryDialog";
import { UserAvatar } from "../components/UserAvatar";
import { HalfStarRating } from "../components/HalfStarRating";
import { Star, ArrowLeft, Calendar, Clock, Film, Tv, Globe, Users, Layers, Plus, Edit, Trash2, BookOpen, Gamepad2, Heart, MessageSquare as MessageSquareIcon, Share2, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";
import { ShareModal } from "../components/ShareModal";
import { SocialShareModal } from "../components/SocialShareModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = '/api';

const mediaTypeIcons = {
  movie: Film, tv_series: Tv, book: BookOpen, video_game: Gamepad2,
};

const statusColors = {
  planned: "bg-gray-500", in_progress: "bg-blue-500", completed: "bg-green-500", dropped: "bg-red-500",
};

export function MediaDetail({ user }) {
  const { t } = useTranslation();
  const mediaTypeConfig = {
    movie: { icon: Film, label: t.mediaTypes.movie },
    tv_series: { icon: Tv, label: t.mediaTypes.tv_series },
    book: { icon: BookOpen, label: t.mediaTypes.book },
    video_game: { icon: Gamepad2, label: t.mediaTypes.video_game },
  };
  const statusConfig = {
    planned: { label: t.statuses.planned, color: "bg-gray-500" },
    in_progress: { label: t.statuses.in_progress, color: "bg-blue-500" },
    completed: { label: t.statuses.completed, color: "bg-green-500" },
    dropped: { label: t.statuses.dropped, color: "bg-red-500" },
  };
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [libraryEntry, setLibraryEntry] = useState(null);
  const [communityStatus, setCommunityStatus] = useState(null);
  const [communityTab, setCommunityTab] = useState("completed");
  const [reviews, setReviews] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [socialShareOpen, setSocialShareOpen] = useState(false);

  useEffect(() => { fetchItemData(); }, [itemId]);

  const fetchItemData = async () => {
    setLoading(true);
    try {
      const [itemRes, libraryRes, communityRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/media/${itemId}`),
        axios.get(`${API}/library/check/${itemId}`),
        axios.get(`${API}/items/${itemId}/community-status`).catch(() => ({ data: { completed: [], in_progress: [], planned: [] } })),
        axios.get(`${API}/items/${itemId}/reviews`).catch(() => ({ data: { reviews: [] } })),
      ]);
      setItem(itemRes.data);
      setLibraryEntry(libraryRes.data.entry);
      setCommunityStatus(communityRes.data);
      setReviews(reviewsRes.data.reviews || []);
    } catch (error) {
      console.error("Error fetching item:", error);
      if (error.response?.status === 404) { toast.error(t.mediaDetail.notFound); navigate("/search"); }
      else toast.error(t.mediaDetail.loadError);
    } finally { setLoading(false); }
  };

  const handleLikeReview = async (entryId, currentlyLiked) => {
    try {
      if (currentlyLiked) {
        await axios.delete(`${API}/reviews/${entryId}/like`);
      } else {
        await axios.post(`${API}/reviews/${entryId}/like`);
      }
      // Refresh reviews to update like counts
      const reviewsRes = await axios.get(`${API}/items/${itemId}/reviews`);
      setReviews(reviewsRes.data.reviews || []);
    } catch (error) {
      const msg = error.response?.data?.detail || "Error";
      if (msg !== "Ya diste like a esta reseña") toast.error(msg);
    }
  };

  const handleRemoveFromLibrary = async () => {
    if (!libraryEntry) return;
    try {
      await axios.delete(`${API}/library/${libraryEntry.entry_id}`);
      toast.success(t.mediaDetail.removed);
      setLibraryEntry(null);
      fetchItemData();
    } catch (error) { toast.error(t.mediaDetail.removeError); }
  };

  if (loading) return (<div className="flex items-center justify-center min-h-[50vh]"><div className="animate-pulse text-muted-foreground">{t.common.loading}</div></div>);
  if (!item) return null;

  const config = mediaTypeConfig[item.media_type] || mediaTypeConfig.movie;
  const TypeIcon = config.icon;
  const genreNames = item.genres?.map(g => typeof g === 'string' ? g : g.name).filter(Boolean) || [];
  const countries = item.production_countries || [];

  // Community rating from AdventureTrack users
  const atRating = item.avg_rating;
  const atRatingCount = item.rating_count || 0;

  // Community status tab data
  const tabConfig = [
    { key: "completed", label: t.mediaDetailExtra.communityTabCompleted, data: communityStatus?.completed || [] },
    { key: "in_progress", label: t.mediaDetailExtra.communityTabInProgress, data: communityStatus?.in_progress || [] },
    { key: "planned", label: t.mediaDetailExtra.communityTabPlanned, data: communityStatus?.planned || [] },
  ];
  const activeTabData = tabConfig.find(t => t.key === communityTab)?.data || [];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="media-detail">
      <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="back-button"><ArrowLeft className="w-4 h-4" />{t.common.back}</Button>

      {item.backdrop_url && (
        <div className="relative -mx-6 md:-mx-8 lg:-mx-12 -mt-4 mb-8">
          <div className="aspect-[21/9] md:aspect-[3/1] overflow-hidden">
            <img src={item.backdrop_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className={item.backdrop_url ? "lg:-mt-32 relative z-10" : ""}>
          <div className="aspect-[2/3] rounded-lg overflow-hidden border border-border bg-muted shadow-xl">
            {item.poster_url || item.cover_image ? <img src={item.poster_url || item.cover_image} alt={item.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><TypeIcon className="w-16 h-16 text-muted-foreground/30" /></div>}
          </div>

          <div className="mt-4 space-y-2">
            {libraryEntry ? (
              <>
                <Select
                  value={libraryEntry.status}
                  onValueChange={async (newStatus) => {
                    try {
                      await axios.put(`${API}/library/${libraryEntry.entry_id}`, { status: newStatus });
                      await fetchItemData();
                      if (newStatus === "completed") {
                        setDialogOpen(true);
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Error");
                    }
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="status-dropdown">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${statusConfig[libraryEntry.status]?.color}`} />
                      <span className="text-sm font-medium">{statusConfig[libraryEntry.status]?.label}</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusConfig).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${cfg.color}`} />
                          {cfg.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {libraryEntry.rating && (
                  <div className="px-3 py-1">
                    <HalfStarRating value={libraryEntry.rating} readOnly size="sm" />
                  </div>
                )}
                <Button className="w-full btn-scale" onClick={() => setDialogOpen(true)} data-testid="edit-library-entry"><Edit className="w-4 h-4 mr-2" />{t.mediaDetail.editEntry}</Button>
                <Button variant="outline" className="w-full text-destructive hover:text-destructive" onClick={handleRemoveFromLibrary} data-testid="remove-from-library"><Trash2 className="w-4 h-4 mr-2" />{t.mediaDetail.removeFromLibrary}</Button>
              </>
            ) : (
              <Button className="w-full btn-scale" onClick={() => setDialogOpen(true)} data-testid="add-to-library"><Plus className="w-4 h-4 mr-2" />{t.mediaDetail.addToLibrary}</Button>
            )}
            <Button variant="outline" className="w-full" onClick={() => setShareOpen(true)} data-testid="share-button">
              <Share2 className="w-4 h-4 mr-2" />{t.mediaDetailExtra.share}
            </Button>
          </div>
        </motion.div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <Badge variant="secondary" className="font-mono uppercase text-xs"><TypeIcon className="w-3 h-3 mr-1" />{config.label}</Badge>
              {item.vote_average > 0 && (
                <div className="flex items-center gap-1" data-testid="api-rating">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold">{item.vote_average.toFixed(1)}</span>
                  {item.vote_count > 0 && <span className="text-sm text-muted-foreground">{t.mediaDetail.votes(item.vote_count)}</span>}
                </div>
              )}
              {atRating > 0 && (
                <div className="flex items-center gap-1.5" data-testid="community-rating">
                  <img src="/favicon.svg" alt="AT" className="w-4 h-4" />
                  <span className="font-semibold">{atRating.toFixed(1)}</span>
                  <span className="text-sm text-muted-foreground">({atRatingCount})</span>
                </div>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="item-title">{item.title}</h1>
            {item.original_title && item.original_title !== item.title && <p className="text-lg text-muted-foreground mb-4">{item.original_title}</p>}
            {item.tagline && <p className="text-muted-foreground italic mb-4">"{item.tagline}"</p>}
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm">
            {item.release_year && <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-4 h-4" /><span>{item.release_year}</span></div>}
            {item.runtime && <div className="flex items-center gap-2 text-muted-foreground"><Clock className="w-4 h-4" /><span>{item.runtime} min</span></div>}
            {item.media_type === "tv_series" && item.number_of_seasons && (
              <div className="flex items-center gap-2 text-muted-foreground"><Layers className="w-4 h-4" /><span>{item.number_of_seasons} {item.number_of_seasons === 1 ? t.mediaDetail.season : t.mediaDetail.seasons}{item.number_of_episodes && ` · ${item.number_of_episodes} ${t.mediaDetail.episodes}`}</span></div>
            )}
            {countries.length > 0 && <div className="flex items-center gap-2 text-muted-foreground"><Globe className="w-4 h-4" /><span>{countries.slice(0, 2).join(", ")}</span></div>}
          </div>

          {genreNames.length > 0 && <div className="flex flex-wrap gap-2">{genreNames.map((genre) => (<Badge key={genre} variant="outline" className="text-xs">{genre}</Badge>))}</div>}

          <Separator />

          {(item.overview || item.description) && <div><h3 className="font-semibold mb-3">{t.mediaDetail.overview}</h3><p className="text-muted-foreground leading-relaxed">{item.overview || item.description}</p></div>}
          {item.creator && <div><h3 className="font-semibold mb-2">{item.media_type === "movie" ? t.mediaDetail.director : t.mediaDetail.creator}</h3><p className="text-muted-foreground">{item.creator}</p></div>}

          {item.cast && item.cast.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" />{t.mediaDetail.cast}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {item.cast.slice(0, 8).map((actor, index) => (
                  <Card key={index} className="overflow-hidden">
                    <div className="flex items-center gap-3 p-2">
                      {actor.profile_url ? <img src={actor.profile_url} alt={actor.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><span className="text-xs text-muted-foreground">{actor.name?.charAt(0)}</span></div>}
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{actor.name}</p>{actor.character && <p className="text-xs text-muted-foreground truncate">{actor.character}</p>}</div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {libraryEntry && (libraryEntry.review || libraryEntry.notes) && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t.mediaDetail.yourNotes}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {libraryEntry.review && <div><h4 className="text-sm font-medium text-muted-foreground mb-1">{t.mediaDetail.review}</h4><p>{libraryEntry.review}</p></div>}
                {libraryEntry.notes && <div><h4 className="text-sm font-medium text-muted-foreground mb-1">{t.mediaDetail.notes}</h4><p className="text-sm text-muted-foreground">{libraryEntry.notes}</p></div>}
              </CardContent>
            </Card>
          )}

          {/* Reviews with Likes */}
          {reviews.length > 0 && (
            <div data-testid="reviews-section">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MessageSquareIcon className="w-4 h-4" />
                {t.mediaDetailExtra.reviews} ({reviews.length})
              </h3>
              <div className="space-y-3">
                {reviews.map((review) => (
                  <Card key={review.entry_id} className="overflow-hidden" data-testid={`review-${review.entry_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <Link to={review.user?.username ? `/profile/${review.user.username}` : "#"} className="flex-shrink-0">
                          <UserAvatar user={review.user} size="sm" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Link to={review.user?.username ? `/profile/${review.user.username}` : "#"} className="text-sm font-semibold hover:underline">
                              {review.user?.username}
                            </Link>
                            {review.rating && <HalfStarRating value={review.rating} readOnly size="sm" />}
                          </div>
                          <p className="text-sm text-muted-foreground">{review.review}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <button
                              onClick={() => handleLikeReview(review.entry_id, review.liked_by_me)}
                              className={`flex items-center gap-1.5 text-xs transition-colors ${
                                review.liked_by_me ? "text-red-500" : "text-muted-foreground hover:text-red-400"
                              }`}
                              data-testid={`like-review-${review.entry_id}`}
                            >
                              <Heart className={`w-3.5 h-3.5 ${review.liked_by_me ? "fill-current" : ""}`} />
                              <span>{review.like_count || 0}</span>
                            </button>
                            {review.updated_at && (
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(review.updated_at).toLocaleDateString("es-ES")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Community Status Section */}
          <div data-testid="community-status-section" className="overflow-hidden">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t.mediaDetailExtra.communityStatus}
            </h3>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 overflow-x-auto -mx-1 px-1 scrollbar-hide" data-testid="community-tabs">
              {tabConfig.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setCommunityTab(tab.key)}
                  data-testid={`community-tab-${tab.key}`}
                  className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                    communityTab === tab.key
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {tab.label} ({tab.data.length})
                </button>
              ))}
            </div>

            {/* Users list */}
            <div className="space-y-2" data-testid="community-users-list">
              {activeTabData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">{t.mediaDetailExtra.communityEmpty}</p>
              ) : (
                activeTabData.map((entry, idx) => (
                  <Link
                    key={idx}
                    to={entry.user?.username ? `/profile/${entry.user.username}` : "#"}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <UserAvatar user={entry.user} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{entry.user?.name || entry.user?.username}</p>
                      <p className="text-xs text-muted-foreground">@{entry.user?.username || "user"}</p>
                    </div>
                    {entry.rating && (
                      <HalfStarRating value={entry.rating} readOnly size="sm" />
                    )}
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <AddToLibraryDialog open={dialogOpen} onOpenChange={setDialogOpen} item={item} existingEntry={libraryEntry} onSuccess={fetchItemData} />
      <ShareModal
        itemId={itemId}
        itemTitle={item?.title}
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        onCreateImage={libraryEntry ? () => setSocialShareOpen(true) : undefined}
      />
      <SocialShareModal
        isOpen={socialShareOpen}
        onClose={() => setSocialShareOpen(false)}
        type={libraryEntry?.status === "in_progress" ? "started" : libraryEntry?.status === "planned" ? "planned" : "completed"}
        username={user?.username || user?.name || "usuario"}
        title={item?.title || ""}
        coverUrl={item?.poster_url || item?.cover_image || ""}
        rating={libraryEntry?.rating}
        mediaType={item?.media_type}
        genre={item?.genres?.[0]?.name || item?.genres?.[0] || ""}
        creator={item?.creator || item?.director || ""}
      />
    </div>
  );
}

export default MediaDetail;
